import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Card,
  CardMedia,
  Grid,
  Alert,
  CircularProgress,
  useTheme,
  alpha,
  Tooltip
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  Add as AddIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import apiService from 'Network/apiService';
import { API_ENDPOINTS } from 'Network/apiEndpoints';
import ConfirmationDialog from 'components/ConfirmationDialog';
import { useToast } from 'OtherReusableFunctionality/ToastContext';

const ImageManagement = ({ open, onClose, product, onImageUpdated }) => {
  const theme = useTheme();
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newImages, setNewImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [deletingImages, setDeletingImages] = useState(new Set());
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pendingDeleteUrl, setPendingDeleteUrl] = useState(null);
  const [oversizedRejectOnly, setOversizedRejectOnly] = useState(false);

  const isBusy = isLoading || deletingImages.size > 0;

  // Get existing product images from product payload
  const getExistingImages = (productData) => {
    if (!productData) return [];

    // If product has multiple images as array, normalize them to objects
    if (productData.images && Array.isArray(productData.images)) {
      return productData.images.map((image, index) =>
        typeof image === 'string'
          ? { url: image, id: `existing_${index}` }
          : image
      );
    }

    // If product has images as comma-separated string
    if (productData.image && typeof productData.image === 'string') {
      const imageUrls = productData.image.split(',').filter(url => url.trim() !== '');
      return imageUrls.map((url, index) => ({
        url: url,
        id: `existing_${index}`
      }));
    }

    return [];
  };

  const MAX_IMAGES = 5;
  const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB per image

  // Reset state when modal opens/closes
  React.useEffect(() => {
    if (open) {
      setNewImages([]);
      setExistingImages(getExistingImages(product));
      setError('');
      setSuccess('');
      setDeletingImages(new Set());
      setDeleteConfirmOpen(false);
      setPendingDeleteUrl(null);
      setOversizedRejectOnly(false);
    }
  }, [open, product]);

  // Image upload with react-dropzone — files over maxSize are rejected and never queued
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/*': ['.jpg','.png','.webp']
    },
    maxFiles: MAX_IMAGES,
    maxSize: MAX_FILE_SIZE_BYTES,
    onDrop: (acceptedFiles, fileRejections) => {
      const hasOversizedRejection = fileRejections.some((r) =>
        r.errors.some((e) => e.code === 'file-too-large')
      );

      const availableSlots = MAX_IMAGES - existingImages.length - newImages.length;

      if (availableSlots <= 0) {
        setError(`You already have ${MAX_IMAGES} images. Delete one before adding more.`);
        return;
      }

      const withinLimit = acceptedFiles.filter((f) => f.size <= MAX_FILE_SIZE_BYTES);
      const filesToAdd = withinLimit.slice(0, availableSlots);

      if (filesToAdd.length === 0) {
        if (hasOversizedRejection) {
          setOversizedRejectOnly(true);
          setError('');
        }
        return;
      }

      setOversizedRejectOnly(false);

      const images = filesToAdd.map((file) => ({
        file,
        preview: URL.createObjectURL(file),
        id: Math.random().toString(36).substr(2, 9)
      }));

      setNewImages((prev) => [...prev, ...images]);
      if (hasOversizedRejection) {
        setError('Each image must be 5MB or smaller.');
      } else {
        setError('');
      }
    }
  });

  // Remove new image (before upload)
  const removeNewImage = (imageId) => {
    setNewImages(prev => {
      const updated = prev.filter(img => img.id !== imageId);
      // Revoke URL to prevent memory leaks
      const removedImage = prev.find(img => img.id === imageId);
      if (removedImage) {
        URL.revokeObjectURL(removedImage.preview);
      }
      return updated;
    });
  };

  const openDeleteConfirm = (imageUrl) => {
    setPendingDeleteUrl(imageUrl);
    setDeleteConfirmOpen(true);
    setError('');
  };

  const handleDeleteConfirmCancel = () => {
    if (pendingDeleteUrl && deletingImages.has(pendingDeleteUrl)) return;
    setDeleteConfirmOpen(false);
    setPendingDeleteUrl(null);
  };

  // Delete existing image (after confirmation)
  const deleteExistingImage = async (imageUrl) => {
    if (!product?.id || !imageUrl) return;

    setDeletingImages(prev => new Set([...prev, imageUrl]));
    setError('');

    try {
      const formData = new FormData();
      formData.append('id', product.id);
      formData.append('image', imageUrl);

      const response = await apiService.post(
        API_ENDPOINTS.POST_add_or_delete_product_images,
        formData,
        "multipart/form-data"
      );

            if (response?.success) {
            toast.showSuccess('Image deleted successfully!');
            setExistingImages((prev) => prev.filter((img) => (img?.url || img) !== imageUrl));
            setDeleteConfirmOpen(false);
            setPendingDeleteUrl(null);
          } else {
        setError(response?.message || 'Failed to delete image');
      }
    } catch (error) {
      console.error('Error deleting image:', error);
      setError(error.message || 'An error occurred while deleting the image');
    } finally {
      setDeletingImages(prev => {
        const updated = new Set(prev);
        updated.delete(imageUrl);
        return updated;
      });
    }
  };

  const handleDeleteConfirm = () => {
    if (pendingDeleteUrl) {
      deleteExistingImage(pendingDeleteUrl);
    }
  };

  // Add new images
  const addNewImages = async () => {
    if (newImages.length === 0) {
      setError('Please select images to upload');
      return;
    }

    if (!product?.id) {
      setError('Product ID not found');
      return;
    }

    const oversized = newImages.some((img) => img.file.size > MAX_FILE_SIZE_BYTES);
    if (oversized) {
      setError('Each image must be 5MB or smaller.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('id', product.id);

      // Add new images
      newImages.forEach(image => {
        formData.append('images', image.file);
      });

      const response = await apiService.post(
        API_ENDPOINTS.POST_add_or_delete_product_images,
        formData,
        "multipart/form-data"
      );

          if (response?.success) {
          toast.showSuccess('Images uploaded successfully!');
          setNewImages([]);
          setTimeout(() => {
            onImageUpdated();
          }, 500);
        } else {
          setError(response?.message || 'Failed to upload images');
        }

    } catch (error) {
      console.error('Error uploading images:', error);
      setError(error.message || 'An error occurred while uploading images');
    } finally {
      setIsLoading(false);
    }
  };

  if (!product) return null;

  const hasOversizedUpload = newImages.some(
    (img) => img.file && img.file.size > MAX_FILE_SIZE_BYTES
  );

  const showOversizedHint = oversizedRejectOnly || hasOversizedUpload;
  const canShowUploadButton = newImages.length > 0 && !hasOversizedUpload;

 const handleManageDialogClose = async (event, reason) => {
  if (isBusy) return;
  // Refresh the data before closing to ensure deleted images don't reappear
  if (onImageUpdated) {
    await onImageUpdated();
  }
  onClose?.(event, reason);
};

  return (
    <>
    <Dialog
      open={open}
      onClose={handleManageDialogClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={isBusy}
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ImageIcon color="primary" />
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Manage Product Images
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {product.name} • SKU: {product.sku}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={() => handleManageDialogClose()} disabled={isBusy}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        {/* Existing Images */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Current Images ({existingImages.length})
          </Typography>
          
          {existingImages.length === 0 ? (
            <Box sx={{ 
              textAlign: 'center', 
              py: 4,
              backgroundColor: alpha(theme.palette.grey[100], 0.3),
              borderRadius: 2,
              border: `1px dashed ${theme.palette.divider}`
            }}>
              <ImageIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                No images uploaded for this product
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {existingImages.map((image, index) => (
                <Grid item xs={6} sm={4} md={3} key={image.id || index}>
                  <Card sx={{ position: 'relative' }}>
                    <CardMedia
                      component="img"
                      height="140"
                      image={image.url}
                      alt={`Product image ${index + 1}`}
                      sx={{ objectFit: 'cover' }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    {/* Fallback for broken images */}
                    <Box
                      sx={{
                        display: 'none',
                        height: 140,
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: alpha(theme.palette.grey[300], 0.5),
                        flexDirection: 'column'
                      }}
                    >
                      <ImageIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">
                        Image not found
                      </Typography>
                    </Box>
                    <Tooltip title="Delete Image">
                      <IconButton
                        size="small"
                        onClick={() => openDeleteConfirm(image.url)}
                        disabled={deletingImages.has(image.url)}
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          backgroundColor: alpha(theme.palette.error.main, 0.8),
                          color: 'white',
                          '&:hover': {
                            backgroundColor: theme.palette.error.main
                          }
                        }}
                      >
                        {deletingImages.has(image.url) ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <DeleteIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>

        {/* Upload New Images */}
        <Box>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Add New Images
          </Typography>
          
          <Box
            {...getRootProps()}
            sx={{
              border: `2px dashed ${isDragActive ? theme.palette.primary.main : theme.palette.divider}`,
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              cursor: 'pointer',
              backgroundColor: isDragActive ? alpha(theme.palette.primary.main, 0.05) : alpha(theme.palette.grey[100], 0.3),
              transition: 'all 0.3s ease',
              mb: 2,
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                borderColor: theme.palette.primary.main
              }
            }}
          >
            <input {...getInputProps()} disabled={isLoading} />
            <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="h6" gutterBottom>
              {isDragActive ? 'Drop images here' : 'Upload New Images'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Drag & drop images here, or click to select files
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Maximum 5 images • Max 5MB each • JPG, PNG, WebP
            </Typography>
            {showOversizedHint && (
              <Typography variant="caption" color="error" display="block" sx={{ mt: 1.5 }}>
                Images over 5MB can&apos;t be uploaded. Choose a smaller file.
              </Typography>
            )}
          </Box>

          {/* New Images Preview */}
          {newImages.length > 0 && (
            <Box>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                New Images to Upload ({newImages.length})
              </Typography>
              <Grid container spacing={2}>
                {newImages.map((image) => (
                  <Grid item xs={6} sm={4} md={3} key={image.id}>
                    <Card sx={{ position: 'relative' }}>
                      <CardMedia
                        component="img"
                        height="120"
                        image={image.preview}
                        alt="New product image"
                        sx={{ objectFit: 'cover' }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => removeNewImage(image.id)}
                        disabled={isLoading}
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          backgroundColor: alpha(theme.palette.error.main, 0.8),
                          color: 'white',
                          '&:hover': {
                            backgroundColor: theme.palette.error.main
                          }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button
          onClick={() => handleManageDialogClose()}
          disabled={isBusy}
          sx={{ textTransform: 'none' }}
        >
          Close
        </Button>
        
        {canShowUploadButton && (
          <Button
            onClick={addNewImages}
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={16} /> : <SaveIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 3
            }}
          >
            {isLoading ? 'Uploading...' : `Upload ${newImages.length} Image${newImages.length > 1 ? 's' : ''}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>

    <ConfirmationDialog
      open={deleteConfirmOpen}
      onClose={handleDeleteConfirmCancel}
      onConfirm={handleDeleteConfirm}
      title="Delete image"
      message="Are you sure you want to delete this image? This cannot be undone."
      confirmButtonText="Delete"
      cancelButtonText="Cancel"
      confirmButtonColor="error"
      isLoading={Boolean(pendingDeleteUrl && deletingImages.has(pendingDeleteUrl))}
    />
    </>
  );
};

export default ImageManagement;